import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { type ApiError, addActionNameToMutationResult, useAxiosInstance } from 'shared/hooks/utils';
import { z } from 'zod/v4';
import { ReasonSchema, ReasonTypeSchema } from './use-load-reasons';

export const CreateReasonVariablesSchema = z.object({
  name: z.string().min(1),
  reasonType: ReasonTypeSchema,
});

export type CreateReasonVariables = z.infer<typeof CreateReasonVariablesSchema>;

export async function createReasonFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: CreateReasonVariables,
) {
  const instance = await axiosInstance;
  const { data } = await instance.post('/reasons', CreateReasonVariablesSchema.parse(variables));
  return ReasonSchema.parse(data);
}

export function useCreateReason() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<z.infer<typeof ReasonSchema>, ApiError, CreateReasonVariables>({
    mutationFn: variables => createReasonFn(axiosInstance, variables),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['reasons'] });
    },
  });

  return addActionNameToMutationResult<
    z.infer<typeof ReasonSchema>,
    ApiError,
    CreateReasonVariables,
    'createReason'
  >('createReason', mutation);
}
